<template src="./CaseSolutionFields.html"></template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { CaseSolution } from "@/models/solution/CaseSolution";
import validationRules from "@/services/validationRules";
import solutionValidationService from "@/services/solutionValidationService";
import { NUMBER_ONLY_REGEX } from "@/consts/validationRegex";

export default defineComponent({
    props: {
        solution: { type: Object as PropType<CaseSolution>, required: true }
    },
    emits: ["update:solution"],
    computed: {
        caseCostValue: {
            get(): string | null {
                return this.solution.caseCost?.toString() ?? null;
            },
            set(value: string | null) {
                if (value && NUMBER_ONLY_REGEX.test(value)) {
                    this.updateSolution({ caseCost: Number(value) });
                } else if (!value) {
                    this.updateSolution({ caseCost: null });
                }
            }
        },
        discountGrantedValue: {
            get(): string | null {
                return this.solution.discountGranted?.toString() ?? null;
            },
            set(value: string | null) {
                if (value && NUMBER_ONLY_REGEX.test(value)) {
                    this.updateSolution({ discountGranted: Number(value) });
                } else if (!value) {
                    this.updateSolution({ discountGranted: null });
                }
            }
        },
        detailedConsaltingValue: {
            get(): boolean {
                return !!this.solution.detailedConsalting;
            },
            set(value: boolean) {
                if (!value) {
                    this.updateSolution({
                        detailedConsalting: null as unknown as string | null
                    });
                }
            }
        }
    },
    data() {
        return {
            localSolution: { ...this.solution },

            caseCost: false,
            caseCostDisabled: false,
            discountGranted: false,
            discountGrantedDisabled: false,
            detailedConsalting: false,
            caseCostFieldValidationRules: [
                validationRules.required("Case cost"),
                validationRules.regex(
                    NUMBER_ONLY_REGEX,
                    "Only numbers are allowed"
                )
            ],
            discountFieldValidationRules: [
                validationRules.required("Discount"),
                validationRules.regex(
                    NUMBER_ONLY_REGEX,
                    "Only numbers are allowed"
                ),
                validationRules.max(100, "Discount")
            ],
            textFieldValidationRules: [
                validationRules.required("Detailed consulting")
            ]
        };
    },
    methods: {
        updateSolution(patch: Partial<CaseSolution>) {
            const next: CaseSolution = {
                ...(this.solution as CaseSolution),
                ...patch
            };
            this.$emit("update:solution", next);
        },
        solutionUpdated(): void {
            this.caseCostDisabled = false;
            this.discountGrantedDisabled = false;
            const solutionDictionary = this.solution as unknown as {
                [K: string]: number | boolean | string | null;
            };
            solutionValidationService.requiredFieldsForCaseCost.forEach(
                (field) => {
                    if (solutionDictionary[field]) {
                        this.caseCost = true;
                        this.caseCostDisabled = true;
                    }
                }
            );
            solutionValidationService.requiredFieldsForDiscountGranted.forEach(
                (field) => {
                    if (solutionDictionary[field]) {
                        this.discountGranted = true;
                        this.discountGrantedDisabled = true;
                    }
                }
            );
        }
    },
    watch: {
        caseCost(value: boolean) {
            if (!value) this.updateSolution({ caseCost: null });
        },
        discountGranted(value: boolean) {
            if (!value) this.updateSolution({ discountGranted: null });
        },
        detailedConsalting(value: boolean) {
            if (!value)
                this.updateSolution({
                    detailedConsalting: null as unknown as string | null
                });
        },
        solution: {
            handler(newVal: typeof this.solution) {
                Object.assign(this.localSolution, newVal);
            },
            deep: true
        },
        localSolution: {
            handler(local: typeof this.localSolution) {
                this.$emit("update:solution", { ...local });
            },
            deep: true
        }
    }
});
</script>
